'use client';

import { Button, buttonVariants } from '@/components/ui/Button';
import Link from 'next/link';
import { cn } from '@/lib/utils/helpers';
import { MainLogoText } from '@/components/MainLogo';
import { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useRoutes } from '@/lib/config/routes';
import { GenerateShipmentData } from '@/lib/API/Database/junk/Updater';

const NavItem = ({ item, isMobile = false, onMobileClick = () => { } }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!item.routes) {
    return (
      <Link
        href={item.link}
        className={cn(
          "text-lg font-bold text-gray-700 hover:text-gray-900 transition-colors",
          isMobile && "text-base"
        )}
        onClick={isMobile ? onMobileClick : undefined}
      >
        {item.title}
      </Link>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full text-base font-bold text-gray-700 hover:text-gray-900 transition-colors"
        >
          {item.title}
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </button>
        {isOpen && (
          <div className="pl-4 space-y-2">
            {item.routes.map((subroute) => (
              <Link
                key={subroute.title}
                href={subroute.link || item.link}
                className="block text-base font-bold text-gray-700 hover:text-gray-900 transition-colors"
                onClick={onMobileClick}
              >
                {subroute.title}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}>
      <div className="relative h-16 flex items-center">
        <button className="flex items-center gap-1 text-lg font-bold text-gray-700 hover:text-gray-900 transition-colors">
          {item.title}
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {isOpen && (
        <div className="absolute left-0 w-[600px] bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-6">
            <h3 className="text-xl font-bold">{item.title}</h3>
            {item.desc && <p className="mt-2 text-gray-600">{item.desc}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4 p-6 pt-0">
            {item.routes.map((subroute) => (
              <Link
                key={subroute.title}
                href={subroute.link || item.link}
                className="group p-4 rounded-lg hover:bg-gray-50 transition-colors flex items-start gap-4"
              >
                {subroute.image && (
                  <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded flex items-center justify-center">
                    <img
                      src={subroute.image}
                      alt={subroute.title}
                      className="w-full h-full object-contain rounded"
                    />
                  </div>
                )}
                <div>
                  <h4 className="font-semibold text-gray-900 group-hover:text-blue-600">{subroute.title}</h4>
                  {subroute.desc && (
                    <p className="mt-1 text-sm text-gray-600">{subroute.desc}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const routes = useRoutes();

  async function performJunkAction() {
    await GenerateShipmentData();
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b">
      <div className="container mx-auto">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex-shrink-0">
            <MainLogoText />
          </div>

          {
            process.env.NEXT_PUBLIC_PROD_INSTANCE !== 'true' && (
              <div className="hidden md:block">
                <nav className="flex items-center gap-6">
                  {routes.routes_marketing.map((item) => (
                    <NavItem key={item.link}
                      item={item} />
                  ))}
                </nav>
              </div>
            )
          }


          <div className="hidden md:flex items-center gap-4">
            {process.env.NEXT_PUBLIC_PROD_INSTANCE !== 'true' && (
              <Link
                href="/auth/login"
                className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'font-bold text-xl bg-blue-900 hover:bg-gray-800 text-white')}
              >
                Access Demo
              </Link>
            )}

            {
              process.env.NEXT_PUBLIC_DEMO_CUSTOMER_ID === 'development' && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full justify-center"
                  onClick={performJunkAction}
                >
                  Run Junk
                </Button>
              )
            }
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden">
            <nav className="flex flex-col py-4 px-4 space-y-4 bg-white border-t">
              {routes.routes_marketing.map((item) => (
                <NavItem
                  key={item.link}
                  item={item}
                  isMobile={true}
                  onMobileClick={() => setIsMenuOpen(false)}
                />
              ))}
              <Link
                href="/auth/login"
                className={cn(
                  buttonVariants({ variant: 'secondary' }),
                  'font-bold w-full justify-center text-white'
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                Access Demo
              </Link>
              {/* <Link
                href="/auth/signup"
                className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'font-bold w-full justify-center')}
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>               */}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};